// apps/web/src/views/AnalisisView.tsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { BarChart3, FileText, Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { toISODateSafe } from '../utils/dateUtils';

import AnalisisService, {
  FiltrosDisponibles,
  AnalisisRequest,
  AnalisisResponse,
} from '../services/analisisService';

// Componentes extraídos
import FiltrosPanel, { FiltrosFormData } from '../components/analisis/FiltrosPanel';
import MapaAnalisis from '../components/analisis/MapaAnalisis';
import ResumenMetricas from '../components/analisis/ResumenMetricas';
import TablaReportes from '../components/analisis/TablaReportes';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const AnalisisView: React.FC = () => {
  const { t } = useTranslation();
  // Estados principales
  const [filtrosDisponibles, setFiltrosDisponibles] = useState<FiltrosDisponibles | null>(null);
  const [analisisActual, setAnalisisActual] = useState<AnalisisResponse | null>(null);
  const [cargandoFiltros, setCargandoFiltros] = useState(true);
  const [cargandoAnalisis, setCargandoAnalisis] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [autoEjecutar, setAutoEjecutar] = useState(false);
  
  // 🆕 Estados para configuración de PDF
  const [mostrarConfigPDF, setMostrarConfigPDF] = useState(false);
  const [configPDF, setConfigPDF] = useState({
    zoom: 13,
    incluirMapa: true,
    incluirMetricas: true,
    titulo: ''
  });

  // Estados del formulario
  const [filtros, setFiltros] = useState<FiltrosFormData>({
    fechaDesde: '',
    fechaHasta: '',
    maquinasIds: [],
    operadoresIds: [],
    faenasIds: [],
    prediosIds: [],
    rodalesIds: [],
    incluirSuperposiciones: false
  });

  // ===============================
  // EFECTOS
  // ===============================

  useEffect(() => {
    cargarFiltrosDisponibles();
  }, []);
  
  // 🆕 Efecto para auto-ejecutar análisis cuando cambian los filtros
  useEffect(() => {
    if (autoEjecutar && filtrosDisponibles && !cargandoAnalisis) {
      // Delay pequeño para evitar múltiples ejecuciones
      const timeoutId = setTimeout(() => {
        ejecutarAnalisis();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [filtros, autoEjecutar, filtrosDisponibles]);

  // ===============================
  // FUNCIONES
  // ===============================

  const cargarFiltrosDisponibles = async () => {
    try {
      setCargandoFiltros(true);
      const filtros = await AnalisisService.obtenerFiltrosDisponibles();
      setFiltrosDisponibles(filtros);
      
      if (filtros.rango_fechas.fecha_min && filtros.rango_fechas.fecha_max) {
        setFiltros(prev => ({
          ...prev,
          fechaDesde: filtros.rango_fechas.fecha_min || '',
          fechaHasta: filtros.rango_fechas.fecha_max || ''
        }));
      }
    } catch (error) {
      console.error('Error cargando filtros:', error);
      toast.error('Error cargando filtros disponibles');
    } finally {
      setCargandoFiltros(false);
    }
  };

  const ejecutarAnalisis = async (enableAutoExecute = false) => {
    try {
      setCargandoAnalisis(true);
      
      // 🆕 Activar auto-ejecución si se solicita
      if (enableAutoExecute) {
        setAutoEjecutar(true);
      }
      
      const request: AnalisisRequest = {
        fecha_desde: filtros.fechaDesde || undefined,
        fecha_hasta: filtros.fechaHasta || undefined,
        maquinas_ids: filtros.maquinasIds.length > 0 ? filtros.maquinasIds : undefined,
        operadores_ids: filtros.operadoresIds.length > 0 ? filtros.operadoresIds : undefined,
        faenas_ids: filtros.faenasIds.length > 0 ? filtros.faenasIds : undefined,
        predios_ids: filtros.prediosIds.length > 0 ? filtros.prediosIds : undefined,
        rodales_ids: filtros.rodalesIds.length > 0 ? filtros.rodalesIds : undefined,
        incluir_superposiciones: filtros.incluirSuperposiciones
      };

      const resultado = await AnalisisService.analizarReportes(request);
      setAnalisisActual(resultado);
      
      // Mostrar información sobre superposiciones si está habilitada
      if (filtros.incluirSuperposiciones && resultado.superposiciones) {
        if (resultado.superposiciones.total_superposiciones > 0) {
          toast.success(`✅ Análisis completado: ${resultado.total_reportes} reportes, ${resultado.total_poligonos} polígonos, ${resultado.total_lineas || 0} líneas\n🔍 Superposiciones: ${resultado.superposiciones.total_superposiciones} detectadas (${resultado.superposiciones.area_total_superpuesta_hectareas.toFixed(2)} ha)`);
        } else {
          toast.success(`✅ Análisis completado: ${resultado.total_reportes} reportes, ${resultado.total_poligonos} polígonos, ${resultado.total_lineas || 0} líneas\n✅ Sin superposiciones detectadas`);
        }
      } else {
        toast.success(`✅ Análisis completado: ${resultado.total_reportes} reportes, ${resultado.total_poligonos} polígonos, ${resultado.total_lineas || 0} líneas`);
      }
    } catch (error) {
      console.error('Error ejecutando análisis:', error);
      toast.error('Error ejecutando análisis');
    } finally {
      setCargandoAnalisis(false);
    }
  };

  const exportarDatos = () => {
    if (!analisisActual) return;
    AnalisisService.exportarAnalisisCSV(analisisActual);
    toast.success('📊 Datos exportados a CSV');
  };

  const abrirConfiguracionPDF = () => {
    // Establecer título por defecto
    setConfigPDF(prev => ({
      ...prev,
      titulo: `Análisis Multi-Criterio - ${new Date().toLocaleDateString()}`
    }));
    setMostrarConfigPDF(true);
  };

  const generarPDFBatch = async () => {
    if (!analisisActual || analisisActual.reportes.length === 0) {
      toast.error('No hay reportes para generar PDF');
      return;
    }

    try {
      setGenerandoPDF(true);
      toast.loading('📝 Generando PDF...', { id: 'pdf-progress' });

      // 🆕 NUEVO ENFOQUE: Usar endpoint que recibe análisis completo
      const url = `/mava/api/v1/analisis/pdf-from-analisis`;
      
      // Obtener token de autenticación
      const token = localStorage.getItem('mava_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      
      // Obtener empresa activa
      const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
      const empresaActiva = empresaActivaStr ? JSON.parse(empresaActivaStr) : null;
      
      // 🆕 NUEVO PAYLOAD: Enviar análisis completo + opciones
      const payload = {
        analisis_completo: analisisActual,
        opciones_pdf: {
          incluir_mapa: configPDF.incluirMapa,
          incluir_metricas: configPDF.incluirMetricas,
          titulo_personalizado: configPDF.titulo,
          zoom_mapa: configPDF.zoom
        },
        lang: i18n.language,
      };  
      // Hacer request POST con el análisis completo
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Intentar leer el error como JSON primero
        const errorText = await response.text();
        
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          // Si no es JSON, usar el texto directamente
          errorData = { message: errorText };
        }
        
        throw new Error((errorData as any).detail || (errorData as any).message || `Error HTTP: ${response.status}`);
      }

      // Verificar que la respuesta sea un PDF
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/pdf')) {
        // Leer el contenido para debugging
        await response.text();
        throw new Error(`La respuesta no es un PDF válido. Content-Type: ${contentType}`);
      }

      // Convertir respuesta a blob
      const blob = await response.blob();
      
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `analisis_multicriterio_${toISODateSafe(new Date())}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL temporal
      URL.revokeObjectURL(link.href);
      
      // 🆕 Toast de éxito
      toast.success(`✅ PDF generado exitosamente (${analisisActual.total_reportes} reportes, ${analisisActual.metricas.por_operador.length} operadores)`, { id: 'pdf-progress' });
      
    } catch (error: any) {
      console.error('Error generando PDF batch:', error);
      // 🆕 Toast de error
      toast.error(`❌ Error generando PDF: ${error.message}`, { id: 'pdf-progress' });
    } finally {
      setGenerandoPDF(false); // 🆕 Desactivar estado de carga
    }
  };

  // ===============================
  // RENDER
  // ===============================

  if (cargandoFiltros) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{t('analysis.multi_criteria')}</h1>
                <p className="text-sm text-gray-500">{t('analysis.title')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {analisisActual && (
                <>
                  <button
                    onClick={exportarDatos}
                    disabled={generandoPDF} // 🆕 Deshabilitar durante PDF
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('common.export')}
                  </button>
                  
                  <button
                    onClick={abrirConfiguracionPDF}  // 🆕 Abrir modal de configuración
                    disabled={generandoPDF}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('pdf.generate')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 Overlay de carga para PDF */}
      {generandoPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto mb-4">
                <Loader2 className="w-12 h-12 text-red-600 animate-spin mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Generando PDF
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Procesando {analisisActual?.reportes.length || 0} reportes con mapas y métricas...
              </p>
              <div className="bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-red-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
              <p className="text-xs text-gray-500">
                Este proceso puede tomar unos momentos
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* 🆕 Modal de Configuración PDF */}
      {mostrarConfigPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📊 Configuración del PDF
            </h3>
            
            <div className="space-y-4">
              {/* Título personalizado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pdf.custom_title')}:
                </label>
                <input
                  type="text"
                  value={configPDF.titulo}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, titulo: e.target.value }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="Título personalizado..."
                />
              </div>
              
              {/* Control de Zoom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nivel de Zoom del Mapa: {configPDF.zoom}
                </label>
                <input
                  type="range"
                  min="8"
                  max="18"
                  value={configPDF.zoom}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, zoom: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>8 (Más lejos)</span>
                  <span>13 (Recomendado)</span>
                  <span>18 (Más cerca)</span>
                </div>
              </div>
              
              {/* Opciones */}
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={configPDF.incluirMapa}
                    onChange={(e) => setConfigPDF(prev => ({ ...prev, incluirMapa: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t('analysis.include_map')}
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={configPDF.incluirMetricas}
                    onChange={(e) => setConfigPDF(prev => ({ ...prev, incluirMetricas: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t('analysis.include_metrics')}
                  </span>
                </label>
              </div>
            </div>
            
            {/* Botones */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setMostrarConfigPDF(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>

              <button
                onClick={() => {
                  setMostrarConfigPDF(false);
                  generarPDFBatch();
                }}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                {t('pdf.generate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* Panel de Filtros */}
          <div className="lg:col-span-1">
            <FiltrosPanel
              filtrosDisponibles={filtrosDisponibles}
              filtros={filtros}
              setFiltros={setFiltros}
              onEjecutarAnalisis={() => ejecutarAnalisis(true)} // 🆕 Activar auto-ejecución
              cargandoAnalisis={cargandoAnalisis}
              autoEjecutar={autoEjecutar} // 🆕 Pasar estado de auto-ejecución
              onToggleAutoEjecutar={setAutoEjecutar} // 🆕 Función para toggle
            />
          </div>

          {/* Panel Principal */}
          <div className="lg:col-span-3">
            {analisisActual ? (
              <div className="space-y-6">
                
                {/* Resumen de Métricas */}
                <ResumenMetricas analisis={analisisActual} />

                {/* Mapa de Análisis */}
                <MapaAnalisis 
                  geometrias={analisisActual.geometrias}
                  centroMapa={analisisActual.centro_mapa}
                />

                {/* Tabla de Reportes */}
                <TablaReportes reportes={analisisActual.reportes} />

              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-12">
                <div className="text-center">
                  <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('analysis.no_results')}</h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Configura los filtros en el panel lateral y ejecuta un análisis para ver los resultados.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">💡 Consejos para el análisis:</h4>
                    <ul className="text-xs text-blue-700 space-y-1 text-left">
                      <li>• Selecciona un rango de fechas para acotar los resultados</li>
                      <li>• Usa filtros específicos (máquinas, operadores, faenas) para análisis detallados</li>
                      <li>• Habilita "Analizar superposiciones" para detectar solapamientos</li>
                      <li>• Los resultados incluyen métricas, mapa interactivo y tabla de reportes</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalisisView;